/**
 * A record's whole detail page -- every tab, the lifecycle buttons -- mounted inside another screen.
 *
 * ONE COMPONENT FOR BOTH PLACES THAT DO THIS (2026-11-04): the bottom record drawer, and the right section of Folders
 * when a record is chosen in the tree (Ed: "the full detail view (the same as what you put into the bottom drawer)
 * will appear in the right section"). Two copies would be two answers to "what does a record look like here".
 *
 * WHICH PAGE: a revision does not say what kind of record it is, so it is read once for its classification -- the same
 * read /object/{usid} makes to choose a route -- and ObjectDetail is mounted with that classification, embedded.
 */
import React, {useEffect, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";

import {Grey7} from "../../theme/styles";
import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {classificationByCode, ObjectClassification} from "../../util/objectClassifications";
import ObjectDetail from "../../pages/ObjectDetail";

export default function EmbeddedRecord({revisionUSID, sessionContext, onOpenRecord, initialPane}: {
    revisionUSID: string;
    /** The tab to raise once the record has loaded. */
    initialPane?: string;
    sessionContext: any;
    /** Another record opened from inside this one: Revise's new draft, "open it" on a draft in progress. */
    onOpenRecord: (revisionUSID: string) => void;
}) {
    const [classification, setClassification] = useState<ObjectClassification | null>(null);
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setClassification(null);
        setProblem(null);
        makeCresAxiosFoundationAPI(sessionContext).get("t1/v1/object_revisions", {params: {USID: revisionUSID}})
            .then((response: any) => {
                if (cancelled) return;
                const list = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
                const row = Array.isArray(list) ? list[0] : list;
                const found = classificationByCode(row?.ObjectClassification);
                if (found) setClassification(found);
                else setProblem("That record could not be opened.");
            })
            .catch(() => { if (!cancelled) setProblem("That record could not be opened."); });
        return () => { cancelled = true; };
    }, [revisionUSID, sessionContext]);

    if (problem) return <Typography sx={{fontSize: 13, color: "#B3261E", p: 3}}>{problem}</Typography>;
    if (!classification) {
        return (
            <Box sx={{display: "flex", alignItems: "center", gap: 1.25, p: 3}}>
                <CircularProgress size={16}/>
                <Typography sx={{fontSize: 13, color: Grey7}}>Reading the record…</Typography>
            </Box>
        );
    }
    // KEYED ON THE RECORD, so opening another starts the page fresh rather than carrying one record's open tab and
    // unsaved edits into the next.
    return <ObjectDetail key={revisionUSID} classification={classification} revisionUSID={revisionUSID} embedded
                         onOpenRecord={onOpenRecord} initialPane={initialPane}/>;
}
